import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import CustomInput from "../CustomInput/CustomInput";
import "./NewProjectForm.scss";
import CustomButton from "../CustomButton/CustomButton";

interface NewProjectFormProps {
    handleClose: () => void;
    handleSubmit: (data: NewProjectFormData) => void;
    isPending: boolean;
}

export interface NewProject {
    projectName: string;
    description: string;
    gitHubLink: string;
    branchName: string;
    thumbnailImage: File | null | undefined;
    bitToken: string | null | undefined;
}


const schema = yup.object({
    projectName: yup.string().trim().required('Project name is required'),
    description: yup.string().trim().required('Description is required'),
    gitHubLink: yup.string().trim().required('GitHub link is required').url('Enter a valid URL'),
    branchName: yup.string().trim().required('Branch name is required'),
    thumbnailImage: yup
        .mixed<File>()
        .nullable()
        .notRequired()
        .test('fileType', 'Only image files are allowed', value => {
            if (value === undefined || value === null) return true;
            // Handle FileList
            if (typeof FileList !== "undefined" && value instanceof FileList) {
                if (value.length === 0) return true;
                return value[0]?.type?.startsWith('image/');
            }
            // Handle array
            if (Array.isArray(value)) {
                if (value.length === 0) return true;
                return value[0]?.type?.startsWith('image/');
            }
            // Handle File
            return value instanceof File ? value.type.startsWith('image/') : false;
        })
        .test('fileSize', 'Image must be less than 1MB', value => {
            if (value === undefined || value === null) return true;
            // Handle FileList
            if (typeof FileList !== "undefined" && value instanceof FileList) {
                if (value.length === 0) return true;
                return value[0]?.size <= 1024 * 1024;
            }
            // Handle array
            if (Array.isArray(value)) {
                if (value.length === 0) return true;
                return value[0]?.size <= 1024 * 1024;
            }
            // Handle File
            return value instanceof File ? value.size <= 1024 * 1024 : false;
        }),
    bitToken: yup
        .string()
        .nullable()
        .notRequired()
        .test('minLength', 'BIT Token must be at least 6 characters', value => {
            if (!value) return true; // skip if not present
            return value.trim().length >= 6;
        }),
});

export type NewProjectFormData = yup.InferType<typeof schema>;

export default function NewProject({ handleClose, handleSubmit, isPending }: Readonly<NewProjectFormProps>) {

    const methods = useForm({
        defaultValues: {
            thumbnailImage: undefined,
            bitToken: undefined
        },
        resolver: yupResolver(schema)
    });

    return (

        <form className="new-project-form-wrapper" onSubmit={methods.handleSubmit(handleSubmit)}>
            <div className="new-project-form-title">New Project</div>
            <div className="new-project-form">
                <FormProvider {...methods}>
                    <CustomInput
                        type="file"
                        label="Thumbnail image"
                        name="thumbnailImage"
                        helperText="Upload a image with max of 1MB"
                        showBorder
                        isGreyBackground
                        fullWidth
                    />
                    <CustomInput
                        type="text"
                        label="Project Name"
                        name="projectName"
                        placeholder="Enter project name"
                        isGreyBackground
                        fullWidth
                        required
                    />
                    <CustomInput
                        type="text"
                        label="Description"
                        name="description"
                        placeholder="Type here"
                        isGreyBackground
                        isMultiLine
                        fullWidth
                        required
                    />
                    <CustomInput
                        type="text"
                        label="GitHub Link"
                        name="gitHubLink"
                        placeholder="Enter github link"
                        isGreyBackground
                        fullWidth
                        required
                    />
                    <CustomInput
                        type="text"
                        label="Branch Name"
                        name="branchName"
                        placeholder="Enter Branch name"
                        isGreyBackground
                        fullWidth
                        required
                    />
                    <CustomInput
                        type="text"
                        label="BIT Token"
                        name="bitToken"
                        placeholder="Enter bit token"
                        isGreyBackground
                        fullWidth={true}
                    />
                </FormProvider>
            </div>
            <div className="new-project-form-footer">
                <CustomButton
                    text="Cancel"
                    border="1px solid #2C2C2C1A"
                    background="#FFF"
                    width="139px"
                    height="48px"
                    onClick={handleClose}
                />
                <CustomButton
                    text="Create"
                    background="#EBA832"
                    width="139px"
                    height="48px"
                    type="submit"
                    disabled={isPending}
                    loading={isPending}
                />
            </div>
        </form>
    )
}